import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Slider,
  Button,
  Grid,
  Divider,
  Alert,
  CircularProgress,
  IconButton,
  Paper,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  Palette,
  Save,
  RestartAlt,
  ViewQuilt as LayoutIcon,
  BlurOn,
  Check,
  Favorite,
} from '@mui/icons-material';
import axios from 'axios';
import { GlassmorphicCard, PrimaryButton, SecondaryButton } from '../styles/sharedStyles';
import { useTranslation } from 'react-i18next';

const PRESET_BG_COLORS = [
  'rgba(8, 12, 24, 0.65)',
  'rgba(10, 15, 30, 0.8)',
  'rgba(0, 0, 0, 0.7)',
  'rgba(255, 255, 255, 0.03)',
  'rgba(20, 20, 25, 0.9)',
];

const PRESET_ACCENT_COLORS = [
  '#00d4ff',
  '#ff4081',
  '#00ff88',
  '#ffaa00',
  '#9c27b0',
  '#ffffff',
];

const PRESET_THEMES = {
  default: {
    card_background: 'rgba(8, 12, 24, 0.65)',
    primary_color: '#00d4ff',
    secondary_color: '#ff4081',
    card_rounding: 28,
    glass_blur: 20,
    background_type: 'animated',
    background_animation: 'pulse',
    background_color: '#030710',
    theme_name: 'default',
  },
  halloween: {
    card_background: 'rgba(20, 0, 40, 0.75)',
    primary_color: '#ff6b00',
    secondary_color: '#8a2be2',
    card_rounding: 12,
    glass_blur: 18,
    background_type: 'animated',
    background_animation: 'grid',
    background_color: '#0a0017',
    theme_name: 'halloween',
  },
  christmas: {
    card_background: 'rgba(0, 15, 0, 0.75)',
    primary_color: '#dc2626',
    secondary_color: '#16a34a',
    card_rounding: 20,
    glass_blur: 18,
    background_type: 'animated',
    background_animation: 'particles',
    background_color: '#001400',
    theme_name: 'christmas',
  },
  cyberpunk: {
    card_background: 'rgba(5, 5, 15, 0.8)',
    primary_color: '#00f0ff',
    secondary_color: '#ff00ff',
    card_rounding: 8,
    glass_blur: 25,
    background_type: 'animated',
    background_animation: 'binary',
    background_color: '#000005',
    theme_name: 'cyberpunk',
  },
  dracula: {
    card_background: 'rgba(22, 16, 48, 0.75)',
    primary_color: '#ff6e96',
    secondary_color: '#8aff80',
    card_rounding: 24,
    glass_blur: 22,
    background_type: 'animated',
    background_animation: 'pulse',
    background_color: '#0a0017',
    theme_name: 'dracula',
  },
  nord: {
    card_background: 'rgba(29, 31, 49, 0.8)',
    primary_color: '#81a1c1',
    secondary_color: '#a3be8c',
    card_rounding: 32,
    glass_blur: 20,
    background_type: 'animated',
    background_animation: 'waves',
    background_color: '#1d1f31',
    theme_name: 'nord',
  },
  sunset: {
    card_background: 'rgba(20, 15, 40, 0.75)',
    primary_color: '#ff6b6b',
    secondary_color: '#feca57',
    card_rounding: 28,
    glass_blur: 20,
    background_type: 'animated',
    background_animation: 'pulse',
    background_color: '#140f28',
    theme_name: 'sunset',
  },
};

const GuiThemeSettings = ({ apiBaseUrl, token }) => {
  const { t } = useTranslation();
  const [settings, setGuiSettings] = useState({
    card_background: 'rgba(8, 12, 24, 0.65)',
    primary_color: '#00d4ff',
    secondary_color: '#ff4081',
    card_rounding: 28,
    glass_blur: 20,
    background_type: 'animated',
    background_animation: 'pulse',
    background_color: '#030710',
    background_gradient: null,
    background_image: null,
    background_opacity: 1
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

   const getActivePresetName = useCallback((s) => {
    for (const [name, preset] of Object.entries(PRESET_THEMES)) {
      let match = true;
      for (const key of ['card_background', 'primary_color', 'secondary_color', 'card_rounding', 'glass_blur', 'background_type', 'background_animation', 'background_color']) {
        if (s[key] !== preset[key]) {
          match = false;
          break;
        }
      }
      if (match) return name;
    }
    return null;
  }, []);

  const activePreset = getActivePresetName(settings);

  const PRESET_PRESET_BACKGROUND_ANIMATIONS = [
    { value: 'pulse', label: 'Pulse Gradient' },
    { value: 'particles', label: 'Floating Particles' },
    { value: 'nebula', label: 'Nebula Drift' },
    { value: 'aurora', label: 'Aurora Waves' },
    { value: 'binary', label: 'Binary Rain' },
    { value: 'constellation', label: 'Constellation' },
    { value: 'waves', label: 'Flowing Waves' },
    { value: 'grid', label: 'Tech Grid' },
    { value: 'none', label: 'Static (No Animation)' },
  ];

  const PRESET_BACKGROUND_TYPES = [
    { value: 'animated', label: 'Animated' },
    { value: 'static', label: 'Static Color' },
    { value: 'gradient', label: 'Gradient' },
    { value: 'image', label: 'Image' },
    { value: 'none', label: 'Minimal' },
  ];

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${apiBaseUrl}/api/gui/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGuiSettings(res.data);
    } catch (err) {
      console.error('Failed to fetch GUI settings', err);
      setError('Failed to load theme settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      const settingsToSave = { ...settings };
      if (activePreset) {
        settingsToSave.theme_name = activePreset;
      }
      await axios.post(`${apiBaseUrl}/api/gui/settings`, settingsToSave, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Cache to localStorage so the theme is available immediately on next load
      try {
        localStorage.setItem('theme_settings', JSON.stringify(settingsToSave));
      } catch (e) {
        console.warn('Failed to cache theme settings', e);
      }
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      // Refresh the page to apply theme changes globally
      // (Alternatively, we could use a context provider for the theme)
      window.location.reload();
    } catch (err) {
      console.error('Failed to save GUI settings', err);
      setError('Failed to save theme settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setGuiSettings({
      card_background: 'rgba(8, 12, 24, 0.65)',
      primary_color: '#00d4ff',
      secondary_color: '#ff4081',
      card_rounding: 28,
      glass_blur: 20,
      background_type: 'animated',
      background_animation: 'pulse',
      background_color: '#030710',
      background_gradient: null,
      background_image: null,
      background_opacity: 1,
      theme_name: 'default'
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress sx={{ color: settings.primary_color }} />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {activePreset
            ? `${t('themes.applied')}: ${t(`themes.${activePreset}`)}. `
            : 'Theme applied successfully! '
          }
          Refreshing...
        </Alert>
      )}

      <GlassmorphicCard sx={{ p: 4 }}>
        <Typography variant="h6" sx={{ color: settings.primary_color, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Palette /> GUI Theme Customization
        </Typography>

         <Grid container spacing={4}>
           {/* Predefined Themes */}
           <Grid item xs={12}>
             <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
               <Palette sx={{ fontSize: 20 }} /> Predefined Themes
             </Typography>
             <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
               {Object.entries(PRESET_THEMES).map(([name, themeData]) => (
                 <Tooltip
                   key={name}
                   title={t(`themes.${name}`)}
                   placement="top"
                 >
                   <Button
                     variant="contained"
                     size="small"
                     onClick={() => setGuiSettings({ ...settings, ...themeData, theme_name: name })}
                     sx={{
                       background: `linear-gradient(135deg, ${themeData.primary_color}, ${themeData.secondary_color})`,
                       borderRadius: '12px',
                       fontWeight: 600,
                       textTransform: 'capitalize',
                       px: 2,
                       py: 1,
                       minWidth: 100,
                       position: 'relative',
                       '&:hover': {
                         filter: 'brightness(1.1)',
                         transform: 'translateY(-2px)',
                       },
                       ...(name === activePreset && {
                         border: '2px solid #ffffff',
                         boxShadow: `0 0 0 2px ${themeData.primary_color}, 0 0 15px ${themeData.primary_color}`,
                       }),
                     }}
                   >
                     {name === activePreset && (
                       <Check sx={{ position: 'absolute', top: 2, right: 4, fontSize: 14 }} />
                     )}
                     {t(`themes.${name}`)}
                   </Button>
                 </Tooltip>
               ))}
             </Box>
           </Grid>

           {/* Active Preset Indicator */}
           {activePreset && (
             <Grid item xs={12}>
               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                 <Chip
                   icon={<Favorite sx={{ fontSize: 14 }} />}
                   label={t(`themes.${activePreset}`)}
                   size="small"
                   sx={{
                     background: 'rgba(0, 212, 255, 0.1)',
                     color: '#00d4ff',
                     border: '1px solid rgba(0, 212, 255, 0.3)',
                     fontWeight: 600,
                     textTransform: 'capitalize',
                   }}
                 />
                 <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                   {t('themes.activePreset')}
                 </Typography>
               </Box>
             </Grid>
           )}

            {/* Background Configuration */}
            <Grid item xs={12}>
              <GlassmorphicCard sx={{ p: { xs: 2, sm: 3 }, mb: 2 }}>
                <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LayoutIcon sx={{ fontSize: 20 }} /> Page Background
                </Typography>

                <Grid container spacing={3}>
                  {/* Background Type */}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', display: 'block', mb: 1 }}>
                      Background Type
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {PRESET_BACKGROUND_TYPES.map((type) => (
                        <Button
                          key={type.value}
                          variant={settings.background_type === type.value ? 'contained' : 'outlined'}
                          size="small"
                          onClick={() => setGuiSettings({ ...settings, background_type: type.value })}
                          sx={{
                            borderRadius: '10px',
                            textTransform: 'capitalize',
                            ...(settings.background_type === type.value && {
                              background: `linear-gradient(135deg, ${settings.primary_color}, ${settings.secondary_color})`,
                            }),
                          }}
                        >
                          {type.label}
                        </Button>
                      ))}
                    </Box>
                  </Grid>

                  {/* Animation Style (only when animated) */}
                  {settings.background_type === 'animated' && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', display: 'block', mb: 1 }}>
                        Animation Style
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {PRESET_PRESET_BACKGROUND_ANIMATIONS.map((anim) => (
                          <Button
                            key={anim.value}
                            variant={settings.background_animation === anim.value ? 'contained' : 'outlined'}
                            size="small"
                            onClick={() => setGuiSettings({ ...settings, background_animation: anim.value })}
                            sx={{
                              borderRadius: '10px',
                              ...(settings.background_animation === anim.value && {
                                background: `linear-gradient(135deg, ${settings.primary_color}, ${settings.secondary_color})`,
                              }),
                            }}
                          >
                            {anim.label}
                          </Button>
                        ))}
                      </Box>
                    </Grid>
                  )}

                  {/* Base Color */}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', display: 'block', mb: 1 }}>
                      Base Color
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <input
                        type="color"
                        value={settings.background_color}
                        onChange={(e) => setGuiSettings({ ...settings, background_color: e.target.value })}
                        style={{ width: 40, height: 36, border: 'none', background: 'none', cursor: 'pointer' }}
                      />
                      <input
                        type="text"
                        value={settings.background_color}
                        onChange={(e) => setGuiSettings({ ...settings, background_color: e.target.value })}
                        placeholder="#030710"
                        style={{
                          background: 'rgba(255,255,255,0.05)',
                          border: '1px solid rgba(255,255,255,0.1)',
                          borderRadius: '8px',
                          color: 'white',
                          padding: '8px 12px',
                          width: '140px'
                        }}
                      />
                    </Box>
                  </Grid>

                  {/* Gradient CSS */}
                  {settings.background_type === 'gradient' && (
                    <Grid item xs={12}>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', display: 'block', mb: 1 }}>
                        Gradient CSS
                      </Typography>
                      <input
                        type="text"
                        placeholder="e.g. linear-gradient(135deg, #00d4ff, #ff4081)"
                        value={settings.background_gradient || ''}
                        onChange={(e) => setGuiSettings({ ...settings, background_gradient: e.target.value })}
                        style={{
                          background: 'rgba(255,255,255,0.05)',
                          border: '1px solid rgba(255,255,255,0.1)',
                          borderRadius: '8px',
                          color: 'white',
                          padding: '8px 12px',
                          width: '100%',
                          fontFamily: 'monospace'
                        }}
                      />
                    </Grid>
                  )}

                  {/* Background Image URL */}
                  {settings.background_type === 'image' && (
                    <Grid item xs={12}>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', display: 'block', mb: 1 }}>
                        Background Image URL
                      </Typography>
                      <input
                        type="text"
                        placeholder="https://example.com/bg.jpg"
                        value={settings.background_image || ''}
                        onChange={(e) => setGuiSettings({ ...settings, background_image: e.target.value })}
                        style={{
                          background: 'rgba(255,255,255,0.05)',
                          border: '1px solid rgba(255,255,255,0.1)',
                          borderRadius: '8px',
                          color: 'white',
                          padding: '8px 12px',
                          width: '100%',
                          fontFamily: 'monospace'
                        }}
                      />
                    </Grid>
                  )}

                  {/* Opacity */}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <BlurOn sx={{ fontSize: 18 }} /> Background Opacity ({Math.round((settings.background_opacity ?? 1) * 100)}%)
                    </Typography>
                    <Slider
                      value={settings.background_opacity ?? 1}
                      min={0.3}
                      max={1}
                      step={0.05}
                      onChange={(e, val) => setGuiSettings({ ...settings, background_opacity: val })}
                      sx={{ color: settings.primary_color, mt: 1 }}
                    />
                  </Grid>

                  {/* Live Background Preview */}
                  <Grid item xs={12}>
                    <Box sx={{ mt: 2, p: 3, borderRadius: '16px', background: 'rgba(0,0,0,0.3)', border: '1px dashed rgba(255,255,255,0.2)' }}>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', display: 'block', mb: 2 }}>LIVE BACKGROUND PREVIEW</Typography>
                      <Box
                        sx={{
                          height: 160,
                          borderRadius: '14px',
                          overflow: 'hidden',
                          position: 'relative',
                          backgroundColor: settings.background_color,
                          ...(settings.background_type === 'gradient' && settings.background_gradient
                            ? { background: settings.background_gradient, backgroundSize: 'cover' }
                            : {}),
                          ...(settings.background_type === 'image' && settings.background_image
                            ? { backgroundImage: `url(${settings.background_image})`, backgroundSize: 'cover', backgroundPosition: 'center' }
                            : {}),
                        }}
                          >
                            {settings.background_type === 'animated' && (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  inset: 0,
                                  background: `radial-gradient(circle at 20% 20%, ${settings.secondary_color}33, transparent 45%), radial-gradient(circle at 80% 10%, ${settings.primary_color}33, transparent 50%), ${settings.background_color}`,
                                }}
                              />
                            )}
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </GlassmorphicCard>
            </Grid>

            {/* Card Background */}
           <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 2 }}>
              Card Background Color
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
              {PRESET_BG_COLORS.map(color => (
                <Box
                  key={color}
                  onClick={() => setGuiSettings({ ...settings, card_background: color })}
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    bgcolor: color,
                    cursor: 'pointer',
                    border: settings.card_background === color ? `2px solid ${settings.primary_color}` : '1px solid rgba(255,255,255,0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: settings.card_background === color ? `0 0 15px ${settings.primary_color}66` : 'none',
                  }}
                >
                  {settings.card_background === color && <Check sx={{ color: settings.primary_color }} />}
                </Box>
              ))}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <input
                  type="text"
                  value={settings.card_background}
                  onChange={(e) => setGuiSettings({ ...settings, card_background: e.target.value })}
                  style={{
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '8px',
                    color: 'white',
                    padding: '8px 12px',
                    width: '200px'
                  }}
                />
              </Box>
            </Box>
          </Grid>

          {/* Primary Color */}
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 2 }}>
              Primary Accent Color
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              {PRESET_ACCENT_COLORS.map(color => (
                <Box
                  key={color}
                  onClick={() => setGuiSettings({ ...settings, primary_color: color })}
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    bgcolor: color,
                    cursor: 'pointer',
                    border: settings.primary_color === color ? '2px solid white' : '1px solid rgba(255,255,255,0.2)',
                  }}
                />
              ))}
              <input
                type="color"
                value={settings.primary_color}
                onChange={(e) => setGuiSettings({ ...settings, primary_color: e.target.value })}
                style={{ width: 32, height: 32, border: 'none', background: 'none', cursor: 'pointer' }}
              />
            </Box>
          </Grid>

          {/* Secondary Color */}
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 2 }}>
              Secondary Accent Color
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              {PRESET_ACCENT_COLORS.map(color => (
                <Box
                  key={color}
                  onClick={() => setGuiSettings({ ...settings, secondary_color: color })}
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    bgcolor: color,
                    cursor: 'pointer',
                    border: settings.secondary_color === color ? '2px solid white' : '1px solid rgba(255,255,255,0.2)',
                  }}
                />
              ))}
              <input
                type="color"
                value={settings.secondary_color}
                onChange={(e) => setGuiSettings({ ...settings, secondary_color: e.target.value })}
                style={{ width: 32, height: 32, border: 'none', background: 'none', cursor: 'pointer' }}
              />
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.1)' }} />
          </Grid>

          {/* Card Rounding */}
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <LayoutIcon sx={{ fontSize: 20 }} /> Card Rounding ({settings.card_rounding}px)
            </Typography>
            <Slider
              value={settings.card_rounding}
              min={0}
              max={48}
              onChange={(e, val) => setGuiSettings({ ...settings, card_rounding: val })}
              sx={{ color: settings.primary_color }}
            />
          </Grid>

          {/* Glass Blur */}
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <BlurOn sx={{ fontSize: 20 }} /> Glass Blur Strength ({settings.glass_blur}px)
            </Typography>
            <Slider
              value={settings.glass_blur}
              min={0}
              max={40}
              onChange={(e, val) => setGuiSettings({ ...settings, glass_blur: val })}
              sx={{ color: settings.primary_color }}
            />
          </Grid>

          {/* Preview */}
          <Grid item xs={12}>
            <Box sx={{ mt: 2, p: 3, borderRadius: '16px', background: 'rgba(0,0,0,0.3)', border: '1px dashed rgba(255,255,255,0.2)' }}>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', display: 'block', mb: 2 }}>LIVE PREVIEW</Typography>
              <Paper
                sx={{
                  p: 3,
                  background: settings.card_background,
                  backdropFilter: `blur(${settings.glass_blur}px)`,
                  borderRadius: `${settings.card_rounding}px`,
                  border: '1px solid rgba(255,255,255,0.1)',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                  maxWidth: 300,
                  mx: 'auto'
                }}
              >
                <Typography sx={{ color: settings.primary_color, fontWeight: 700, mb: 1 }}>Accent Primary</Typography>
                <Typography sx={{ color: 'white', mb: 2 }}>Sample text on glass background.</Typography>
                <Button
                  variant="contained"
                  sx={{
                    bgcolor: settings.secondary_color,
                    borderRadius: '8px',
                    '&:hover': { bgcolor: settings.secondary_color, filter: 'brightness(1.1)' }
                  }}
                >
                  Secondary Button
                </Button>
              </Paper>
            </Box>
          </Grid>

           {/* Actions */}
           <Grid item xs={12} sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2, alignItems: 'center' }}>
             {activePreset && (
               <Chip
                 label={t(`themes.${activePreset}`)}
                 size="small"
                 sx={{
                   background: 'rgba(0, 212, 255, 0.08)',
                   color: '#00d4ff',
                   border: '1px solid rgba(0, 212, 255, 0.3)',
                   fontWeight: 600,
                   textTransform: 'capitalize',
                   mr: 'auto',
                 }}
               />
             )}
             <SecondaryButton onClick={handleReset} startIcon={<RestartAlt />}>
               Reset
             </SecondaryButton>
             <PrimaryButton
               onClick={handleSave}
               disabled={saving}
               startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
             >
               {saving ? 'Saving...' : 'Apply Theme'}
             </PrimaryButton>
           </Grid>
        </Grid>
      </GlassmorphicCard>
    </Box>
  );
};

export default GuiThemeSettings;
